import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/");
  }, [user, nav]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success("Account created! Check your email to verify.");
      nav("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0B132B]">Create your account</h2>
      <p className="text-slate-500 text-sm mt-1.5 mb-6">Start invoicing in minutes — free to get started.</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input id="name" required value={form.name} onChange={set("name")} placeholder="Jane Dlamini" className="mt-1.5 h-11" data-testid="register-name-input" />
        </div>
        <div>
          <Label htmlFor="email">Email address</Label>
          <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.co.za" className="mt-1.5 h-11" data-testid="register-email-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={form.password} onChange={set("password")} placeholder="Min 8 chars" className="mt-1.5 h-11" data-testid="register-password-input" />
          </div>
          <div>
            <Label htmlFor="confirm">Confirm</Label>
            <Input id="confirm" type="password" required value={form.confirm} onChange={set("confirm")} placeholder="Repeat" className="mt-1.5 h-11" data-testid="register-confirm-input" />
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full bg-[#0B132B] hover:bg-[#1C2541] h-11 font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
      <p className="text-sm text-slate-500 mt-6 text-center">
        Already have an account?{" "}
        <Link to="/login" className="text-[#0B132B] font-semibold hover:text-[#D4AF37]" data-testid="go-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
